import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { BrandMark } from '../../components/BrandMark';
import type { ToolboxRoute } from '../types';

const productLinks = [
  { label: '小伴清单', href: 'https://together.wyanhub.com/' },
  { label: '表单模板', href: import.meta.env.VITE_FORMS_TEMPLATES_URL || 'https://forms.wyanhub.com/#/templates' },
  { label: '微境沙盘', href: 'https://wyanhub.com/microcosm/' },
  { label: '闲钓小栈', href: 'https://fishing.wyanhub.com/' },
];

interface ToolboxHeaderProps {
  route: ToolboxRoute;
  onNavigate: (path: string) => void;
  commandOpen: boolean;
}

export function ToolboxHeader({ route, onNavigate, commandOpen }: ToolboxHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { setMenuOpen(false); }, [route, commandOpen]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1061px)');
    const closeOnDesktop = () => { if (desktop.matches) setMenuOpen(false); };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !headerRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      menuButtonRef.current?.focus();
    };
    const onFocusOut = (event: FocusEvent) => {
      if (event.relatedTarget instanceof Node && !headerRef.current?.contains(event.relatedTarget)) setMenuOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    const header = headerRef.current;
    header?.addEventListener('focusout', onFocusOut);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
      header?.removeEventListener('focusout', onFocusOut);
    };
  }, [menuOpen]);

  function followLink(event: MouseEvent<HTMLAnchorElement>, path: string) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    setMenuOpen(false);
    onNavigate(path);
  }

  const inTools = ['home', 'category', 'tool'].includes(route.kind);
  const inWorkflows = route.kind === 'workflows' || route.kind === 'workflow';
  const inRelax = route.kind.startsWith('relax');

  return <header className="toolbox-topbar" ref={headerRef}>
    <a className="toolbox-brand" href="/tools" onClick={(event) => followLink(event, '/tools')}>
      <BrandMark /><strong>微言工具箱</strong>
    </a>
    <button className="toolbox-menu-toggle" aria-controls="toolbox-primary-nav" aria-expanded={menuOpen} aria-label={menuOpen ? '关闭菜单' : '打开菜单'} onClick={() => setMenuOpen((value) => !value)} ref={menuButtonRef} type="button">
      {menuOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
    </button>
    <nav aria-label="主导航" className={menuOpen ? 'is-open' : ''} id="toolbox-primary-nav">
      <div className="toolbox-section-links">
        <a aria-current={inTools ? route.kind === 'home' ? 'page' : 'location' : undefined} href="/tools" onClick={(event) => followLink(event, '/tools')}>全部工具</a>
        <a aria-current={inWorkflows ? route.kind === 'workflows' ? 'page' : 'location' : undefined} href="/workflows" onClick={(event) => followLink(event, '/workflows')}>组合工作流</a>
        <a aria-current={inRelax ? route.kind === 'relax' ? 'page' : 'location' : undefined} href="/relax" onClick={(event) => followLink(event, '/relax')}>松弛休闲</a>
      </div>
      <div aria-label="微言产品" className="toolbox-product-links">
        {productLinks.map(({ label, href }) => <a aria-label={`${label}（新标签页打开）`} href={href} key={label} rel="noopener noreferrer" target="_blank" title="新标签页打开">{label}<ArrowUpRight size={13} aria-hidden="true" /></a>)}
      </div>
    </nav>
  </header>;
}
